import { z } from 'zod'

import { ModuleTypeEnum } from './module-type'

export enum ExpenseKindEnum {
  REIMBURSEMENT = 'REIMBURSEMENT',
  COMPANY_PAID = 'COMPANY_PAID',
}

export enum ExpenseWorkflowStatusEnum {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  REJECTED = 'REJECTED',
  CANCELED = 'CANCELED',
}

export enum ExpenseReimbursementStatusEnum {
  NOT_REQUIRED = 'NOT_REQUIRED',
  PENDING = 'PENDING',
  PARTIALLY_REIMBURSED = 'PARTIALLY_REIMBURSED',
  REIMBURSED = 'REIMBURSED',
}

export enum ExpenseContextRoleEnum {
  PRIMARY = 'PRIMARY',
  TAG = 'TAG',
}

export enum ExpenseStatusActionEnum {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  SUBMITTED = 'SUBMITTED',
  RECALLED = 'RECALLED',
  APPROVED = 'APPROVED',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  REJECTED = 'REJECTED',
  REIMBURSEMENT_RECORDED = 'REIMBURSEMENT_RECORDED',
  CANCELED = 'CANCELED',
}

export enum ExpenseSortFieldEnum {
  EXPENSE_DATE = 'expenseDate',
  TOTAL_AMOUNT = 'totalAmount',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
  MERCHANT = 'merchant',
  WORKFLOW_STATUS = 'workflowStatus',
  REIMBURSEMENT_STATUS = 'reimbursementStatus',
}

export const ExpenseContextModuleSchema = z.union([
  z.literal(ModuleTypeEnum.ENTITY),
  z.literal(ModuleTypeEnum.PROJECT),
  z.literal(ModuleTypeEnum.MEMBER),
])

export const ExpenseCategorySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  order: z.number().default(0),
  isActive: z.boolean().default(true),
  receiptRequired: z.boolean().default(false),
  defaultChartOfAccountId: z.string().uuid().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type ExpenseCategory = z.infer<typeof ExpenseCategorySchema>

export const DEFAULT_EXPENSE_CATEGORIES = [
  {
    name: 'Travel',
    description: 'Flights, hotels, rideshare, mileage, and other travel costs.',
    color: '#3B82F6',
    receiptRequired: true,
  },
  {
    name: 'Meals',
    description: 'Team meals, client lunches, coffee, and catering.',
    color: '#F59E0B',
    receiptRequired: true,
  },
  {
    name: 'Office & equipment',
    description:
      'Office supplies, hardware, peripherals, and workspace purchases.',
    color: '#6366F1',
    receiptRequired: true,
  },
  {
    name: 'Software & subscriptions',
    description: 'SaaS tools, subscriptions, licenses, and digital services.',
    color: '#8B5CF6',
    receiptRequired: true,
  },
  {
    name: 'Marketing',
    description:
      'Ads, events, printing, promotional items, and campaign spend.',
    color: '#EC4899',
    receiptRequired: true,
  },
  {
    name: 'Training & education',
    description: 'Courses, conferences, books, certifications, and workshops.',
    color: '#10B981',
    receiptRequired: true,
  },
  {
    name: 'Client delivery',
    description:
      'Production materials, fulfillment, and client-specific purchases.',
    color: '#F97316',
    receiptRequired: true,
  },
  {
    name: 'Miscellaneous',
    description:
      'Other approved business expenses that do not fit a standard category.',
    color: '#6B7280',
    receiptRequired: false,
  },
] as const

export const ExpenseLinkSchema = z.object({
  id: z.string().uuid(),
  expenseId: z.string().uuid(),
  organizationId: z.string().uuid(),
  moduleType: ExpenseContextModuleSchema,
  moduleId: z.string().uuid(),
  role: z.nativeEnum(ExpenseContextRoleEnum),
  moduleName: z.string().nullable().optional(),
  createdAt: z.date(),
})

export type ExpenseLink = z.infer<typeof ExpenseLinkSchema>

export const ExpenseReimbursementSchema = z.object({
  id: z.string().uuid(),
  expenseId: z.string().uuid(),
  organizationId: z.string().uuid(),
  amount: z.number(),
  reimbursedAt: z.date(),
  reference: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  createdBy: z.string().uuid(),
  createdAt: z.date(),
})

export type ExpenseReimbursement = z.infer<typeof ExpenseReimbursementSchema>

export const ExpenseStatusHistorySchema = z.object({
  id: z.string().uuid(),
  expenseId: z.string().uuid(),
  organizationId: z.string().uuid(),
  action: z.nativeEnum(ExpenseStatusActionEnum),
  performedBy: z.string().uuid(),
  previousWorkflowStatus: z
    .nativeEnum(ExpenseWorkflowStatusEnum)
    .nullable()
    .optional(),
  newWorkflowStatus: z.nativeEnum(ExpenseWorkflowStatusEnum),
  previousWorkflowStageId: z.string().nullable().optional(),
  newWorkflowStageId: z.string(),
  previousReimbursementStatus: z
    .nativeEnum(ExpenseReimbursementStatusEnum)
    .nullable()
    .optional(),
  newReimbursementStatus: z
    .nativeEnum(ExpenseReimbursementStatusEnum)
    .nullable()
    .optional(),
  comment: z.string().nullable().optional(),
  snapshot: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.date(),
})

export type ExpenseStatusHistory = z.infer<typeof ExpenseStatusHistorySchema>

const BaseExpenseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  memberId: z.string().uuid(),
  categoryId: z.string().uuid().nullable().optional(),
  chartOfAccountId: z.string().uuid().nullable().optional(),
  taxCategoryId: z.string().uuid().nullable().optional(),
  kind: z.nativeEnum(ExpenseKindEnum).default(ExpenseKindEnum.REIMBURSEMENT),
  workflowStatus: z
    .nativeEnum(ExpenseWorkflowStatusEnum)
    .default(ExpenseWorkflowStatusEnum.DRAFT),
  workflowStageId: z.string().default('draft'),
  reimbursementStatus: z
    .nativeEnum(ExpenseReimbursementStatusEnum)
    .default(ExpenseReimbursementStatusEnum.PENDING),
  merchant: z.string(),
  description: z.string().nullable().optional(),
  expenseDate: z.date(),
  currency: z.string().default('USD'),
  subtotal: z.number(),
  taxAmount: z.number().default(0),
  tipAmount: z.number().default(0),
  totalAmount: z.number(),
  billable: z.boolean().default(false),
  reimbursedAmount: z.number().default(0),
  paymentMethod: z.string().nullable().optional(),
  receiptRequired: z.boolean().default(false),
  reimbursementReference: z.string().nullable().optional(),
  reimbursementNote: z.string().nullable().optional(),
  receiptCount: z.number().int().nonnegative().optional(),
  submittedAt: z.date().nullable().optional(),
  approvedAt: z.date().nullable().optional(),
  approvedBy: z.string().uuid().nullable().optional(),
  rejectedAt: z.date().nullable().optional(),
  rejectedBy: z.string().uuid().nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  reimbursedAt: z.date().nullable().optional(),
  reimbursedBy: z.string().uuid().nullable().optional(),
  contractorInvoiceId: z.string().uuid().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  links: z.array(ExpenseLinkSchema).optional(),
  category: ExpenseCategorySchema.nullable().optional(),
  reimbursements: z.array(ExpenseReimbursementSchema).optional(),
  history: z.array(ExpenseStatusHistorySchema).optional(),
})

export const ExpenseSchema = BaseExpenseSchema.refine(
  data => data.totalAmount >= data.subtotal,
  {
    message: 'Total amount must be greater than or equal to subtotal',
    path: ['totalAmount'],
  },
)

export type Expense = z.infer<typeof ExpenseSchema>

export const CreateExpenseSchema = BaseExpenseSchema.omit({
  id: true,
  organizationId: true,
  memberId: true,
  workflowStatus: true,
  workflowStageId: true,
  reimbursementStatus: true,
  reimbursedAmount: true,
  reimbursementReference: true,
  reimbursementNote: true,
  receiptCount: true,
  submittedAt: true,
  approvedAt: true,
  approvedBy: true,
  rejectedAt: true,
  rejectedBy: true,
  rejectionReason: true,
  reimbursedAt: true,
  reimbursedBy: true,
  contractorInvoiceId: true,
  createdAt: true,
  updatedAt: true,
  billable: true,
  links: true,
  category: true,
  reimbursements: true,
  history: true,
}).extend({
  billable: z.boolean().optional(),
  links: z.array(
    ExpenseLinkSchema.omit({
      id: true,
      expenseId: true,
      organizationId: true,
      createdAt: true,
    }),
  ),
})

export type CreateExpense = z.infer<typeof CreateExpenseSchema>

export const CreateExpenseDraftSchema = CreateExpenseSchema.partial()

export type CreateExpenseDraft = z.infer<typeof CreateExpenseDraftSchema>

export const UpdateExpenseSchema = CreateExpenseSchema.partial().extend({
  workflowStageId: z.string().optional(),
})

export type UpdateExpense = z.infer<typeof UpdateExpenseSchema>

export const RecordExpenseReimbursementSchema = z.object({
  amount: z.number().positive(),
  reimbursedAt: z.date(),
  reference: z.string().optional(),
  note: z.string().optional(),
})

export type RecordExpenseReimbursement = z.infer<
  typeof RecordExpenseReimbursementSchema
>

export const ExpenseFiltersSchema = z.object({
  search: z.string().optional(),
  kind: z
    .union([
      z.nativeEnum(ExpenseKindEnum),
      z.array(z.nativeEnum(ExpenseKindEnum)),
    ])
    .optional(),
  workflowStatus: z
    .union([
      z.nativeEnum(ExpenseWorkflowStatusEnum),
      z.array(z.nativeEnum(ExpenseWorkflowStatusEnum)),
    ])
    .optional(),
  reimbursementStatus: z
    .union([
      z.nativeEnum(ExpenseReimbursementStatusEnum),
      z.array(z.nativeEnum(ExpenseReimbursementStatusEnum)),
    ])
    .optional(),
  memberId: z.string().uuid().optional(),
  categoryId: z.string().uuid().optional(),
  moduleType: ExpenseContextModuleSchema.optional(),
  moduleId: z.string().uuid().optional(),
  mineOnly: z.boolean().optional(),
  missingReceiptOnly: z.boolean().optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
})

export type ExpenseFilters = z.infer<typeof ExpenseFiltersSchema>

export const ExpenseAggregateGroupBySchema = z.enum([
  'none',
  'category',
  'project',
  'entity',
  'month',
])

export type ExpenseAggregateGroupBy = z.infer<
  typeof ExpenseAggregateGroupBySchema
>

export const ExpenseAggregateFiltersSchema = z.object({
  from: z.string(),
  to: z.string(),
  entityIds: z.array(z.string().uuid()).optional(),
  projectIds: z.array(z.string().uuid()).optional(),
  groupBy: ExpenseAggregateGroupBySchema.optional().default('none'),
})

export type ExpenseAggregateFilters = z.infer<
  typeof ExpenseAggregateFiltersSchema
>

export interface ExpenseAggregateGroup {
  key: string
  label: string
  total: number
  count: number
}

export interface ExpenseCategoryBudgetRow {
  categoryId: string | null
  categoryName: string
  actual: number
  budget: number | null
}

export interface ExpenseAggregateResponse {
  total: number
  count: number
  currency: string
  groups: ExpenseAggregateGroup[]
  categoryBudget?: ExpenseCategoryBudgetRow[]
}
